import { MAX_MODIFIER_QUANTITY } from '../constants/modifierQuantity';
import { ParsedModifierGroup } from '../types/menu';

/**
 * One slot of a brand-menu constraint the parser split into `<name>_1..N`
 * single-pick groups (`min === max > 1`, the Baker's Dozen `bagel_choice`).
 * The split keeps `name` bare and suffixes `id`, which is what tells a slot
 * from a constraint that merely ends in a number.
 */
export const isSplitSlotGroup = (
  modGroup: Pick<ParsedModifierGroup, 'id' | 'name'>
): boolean => {
  const id = String(modGroup.id ?? '');
  const prefix = `${modGroup.name}_`;
  return id.startsWith(prefix) && /^\d+$/.test(id.slice(prefix.length));
};

/**
 * HITL-1756 — how many of one option an agent may ring. The one place the rule
 * lives, so a per-option limit from the brand menu (VI-1507) changes only this.
 *
 * - Brand menu only: the count travels as the modifier's `quantity` in the
 *   Menu Module order, which only the brand-menu path builds.
 * - Never on a split slot: a dozen is counted by filling slots, which the
 *   repeated-slot panel owns. A count on one slot would send "4 plain" as the
 *   third bagel.
 * - Every other option takes up to {@link MAX_MODIFIER_QUANTITY}.
 */
export const getModifierMaxQuantity = (
  modGroup: Pick<ParsedModifierGroup, 'id' | 'name'>,
  isBrandMenu: boolean
): number => {
  if (!isBrandMenu || isSplitSlotGroup(modGroup)) return 1;
  return MAX_MODIFIER_QUANTITY;
};

export const clampModifierQuantity = (quantity: number, max: number): number =>
  Math.min(Math.max(Math.round(quantity) || 1, 1), max);
